import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { Observable, of, Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer, Meta, SafeHtml, Title } from '@angular/platform-browser';

import { LanguageService } from '../../../../services/language.service';
import { BrandsService } from '../../../../services/brands.service';
import { BrandProgramResponse, BrandProject } from '../../../../shared/brands.model';
import { CustomValidators } from '../../../../shared/custom-validators';
import { RouterUrls } from '../../../../shared/router-urls';
import { TagsService } from 'src/app/services/tags.service';
import { matchOtherValidator } from '../../../../utils/customValidators';
import { isMobile } from '../../../../utils/screen.utils';
import { Language } from '../../../../shared/projects.model';

@Component({
  selector: 'app-brand-program-page',
  templateUrl: './brand-program-page.component.html',
  styleUrls: ['./brand-program-page.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class BrandProgramPageComponent implements OnInit, OnDestroy {

  private subscriptions = new Subscription();
  programInfo$: Observable<BrandProgramResponse>;
  programInfo: BrandProgramResponse;
  step = 1;
  chosenProject: BrandProject;
  clickEventSubscription: Subscription;
  isLoading = true;
  RouterUrls = RouterUrls;
  status: number;
  screenWidth: number;
  cachedSvgNoosLogo: SafeHtml | null = null;
  // Form related variables
  form: FormGroup;
  emailControl = new FormControl('', [Validators.required, Validators.pattern(CustomValidators.emailValidationRegex)]);
  confirmEmailControl = new FormControl('', [Validators.required, matchOtherValidator('email')]);
  termsCheckedControl = new FormControl(false, Validators.requiredTrue);
  showErrors = false;
  lastError;
  errorMessage;
  disabledButton = false;


  constructor(private languageService: LanguageService,
              private brandsService: BrandsService,
              private activatedRoute: ActivatedRoute,
              private router: Router,
              private meta: Meta,
              private tagsService: TagsService,
              private titleService: Title,
              private dialog: MatDialog,
              private sanitizer: DomSanitizer,
              private fb: FormBuilder) {
    // Initialize form
    this.form = this.fb.group({
      email: this.emailControl,
      confirmEmail: this.confirmEmailControl,
      termsChecked: this.termsCheckedControl
    });

    this.programInfo$ = activatedRoute.params
      .pipe(
        // Get brand-program info and save to variable;
        switchMap(param => this.brandsService.getProgramInfo(param.program)),
        tap(value => {
          this.programInfo = value;
          this.updateTags();
          for (const project of this.programInfo.projects) {
            project.read_more_clicked = false;
          }
          this.isLoading = false;
        }),
        // Redirect to brand page if there is no project with such id;
        catchError((error) => {
          this.router.navigate(['../'], { relativeTo: this.activatedRoute });
          this.isLoading = false;
          return of(error);
        }),
      );
    this.clickEventSubscription = this.tagsService.getClickEventBrandProgram().subscribe(() => {
      this.updateTags();
    });
  }

  get currentLanguage() {
    return this.languageService.getUserUsedLanguage();
  }

  get currentLang() {
    return this.languageService.getCurrentLangWithSlash();
  }


  ngOnInit(): void {
    this.brandsService.isBrandPage.next(true);
    this.screenWidth = window.innerWidth;

    // Handles form and fields changes to dynamically set error messages.
    this.subscriptions.add(
      this.form.get('email').valueChanges.subscribe(() => {
        this.form.get('confirmEmail').updateValueAndValidity();
      })
    );
    this.subscriptions.add(
      this.form.statusChanges.subscribe({
        next: () => {
          this.setErrorMessage();
        }
      })
    );
  }

  previousStep(el: HTMLElement) {
    this.step--;
    setTimeout(() => {
      if (window.innerWidth < 991) {
        el.scrollIntoView();
      }
    }, 0);
  }

  nextStep(el: HTMLElement) {
    this.step++;
    setTimeout(() => {
      if (window.innerWidth < 991) {
        el.scrollIntoView();
      }
    }, 0);
  }

  updateTags() {
    this.titleService.setTitle(this.programInfo.title[this.currentLanguage]);
    this.meta.updateTag({ name: 'description', content: this.programInfo.subtitle[this.currentLanguage] });
    this.meta.updateTag({ property: 'og:title', content: this.programInfo.title[this.currentLanguage] });
    this.meta.updateTag({ property: 'og:description', content: this.programInfo.subtitle[this.currentLanguage] });
    this.meta.updateTag({ property: 'og:type', content: 'URL' });
    this.meta.updateTag({ property: 'og:url', content: window.location.href });
    this.meta.updateTag({ property: 'og:image', content: this.programInfo.picture_cover });
  }

  validateImpact(): void {
    this.disabledButton = true;

    // Validates the current state of the form, marking all fields as touched
    // to trigger validation messages, and sets the appropriate error messages.
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.setErrorMessage();
      this.showErrors = true;
      this.disabledButton = false;
      return;
    }

    // Proceed with form submission if the form is valid
    const payload = {
      email: this.form.value.email,
      customers_pledge_id: this.programInfo.customers_pledge_id,
      project_id: this.chosenProject.project_id,
      language: this.currentLanguage
    };

    this.brandsService.validateImpact(payload).subscribe((responseCode) => {
      this.disabledButton = false;
      this.status = responseCode;
      this.step++;
    });
  }

  returnToFirstStep(target) {
    this.chosenProject = null;
    this.previousStep(target);
  }

  chooseProject(project, target) {
    this.chosenProject = project;
    this.nextStep(target);
  }

  toggleReadMore(projectId: number) {
    for (const project of this.programInfo.projects) {
      if (project.project_id === projectId) {
        project.read_more_clicked = !project.read_more_clicked;
      }
    }
  }

  getRGBAColor(opacity: number) {
    const [r, g, b] = this.programInfo.color.match(/\w\w/g).map(x => parseInt(x, 16));
    return `rgba(${ r }, ${ g }, ${ b }, ${ opacity })`;
  }

  getSvgNoosLogo(): SafeHtml {
    if (!this.cachedSvgNoosLogo) {
      const svgString = isMobile() ? `<svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 60 60" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M30.0152 0C46.5719 0 60 13.4406 60 30C60 46.5594 46.5719 60 30.0152 60C13.4383 60 0 46.5594 0 30C0.0101574 13.4406 13.4383 0 30.0152 0ZM10.8989 41.3986C10.9294 41.3986 19.2788 41.3986 19.2788 41.3986C16.2316 38.5439 14.3525 34.4903 14.3525 30C14.3525 21.3546 21.3611 14.3346 30.0152 14.3346C38.6491 14.3346 45.6577 21.3546 45.6577 30C45.6577 34.4903 43.7786 38.5439 40.7618 41.3986C45.3022 41.3986 46.1452 41.3986 49.1112 41.3986C50.2793 41.3986 51.2443 42.3535 51.2443 43.532C51.2443 44.7105 50.2793 45.6654 49.1112 45.6654H10.8989C9.73083 45.6654 8.76587 44.7105 8.76587 43.532C8.76587 42.3535 9.73083 41.3986 10.8989 41.3986ZM30.0152 18.6522C36.2621 18.6522 41.3611 23.742 41.3611 30.0102C41.3611 36.2784 36.2621 41.3681 30.0152 41.3681C23.7481 41.3681 18.6694 36.2784 18.6694 30.0102C18.6694 23.7318 23.7481 18.6522 30.0152 18.6522Z" fill="${ this.programInfo.color }"/></svg>` :
        `<svg xmlns="http://www.w3.org/2000/svg" width="76" height="76" viewBox="0 0 76 76" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M38.0193 0C58.991 0 76 17.0247 76 38C76 58.9753 58.991 76 38.0193 76C17.0218 76 0 58.9753 0 38C0.0128661 17.0247 17.0218 0 38.0193 0ZM13.8053 52.4382C13.8439 52.4382 24.4198 52.4382 24.4198 52.4382C20.56 48.8222 18.1798 43.6878 18.1798 38C18.1798 27.0491 27.0574 18.1571 38.0193 18.1571C48.9555 18.1571 57.8331 27.0491 57.8331 38C57.8331 43.6878 55.4528 48.8222 51.6316 52.4382C57.3828 52.4382 58.4506 52.4382 62.2075 52.4382C63.6871 52.4382 64.9094 53.6478 64.9094 55.1405C64.9094 56.6333 63.6871 57.8429 62.2075 57.8429H13.8053C12.3257 57.8429 11.1034 56.6333 11.1034 55.1405C11.1034 53.6478 12.3257 52.4382 13.8053 52.4382ZM38.0193 23.6261C45.9319 23.6261 52.3907 30.0731 52.3907 38.0129C52.3907 45.9526 45.9319 52.3996 38.0193 52.3996C30.0809 52.3996 23.6479 45.9526 23.6479 38.0129C23.6479 30.0603 30.0809 23.6261 38.0193 23.6261Z" fill="${ this.programInfo.color }"/></svg>`;
      this.cachedSvgNoosLogo = this.sanitizer.bypassSecurityTrustHtml(svgString);
    }
    return this.cachedSvgNoosLogo;
  }

  redirectToProjects() {
    const url = this.languageService.getCurrentLangWithSlash() + '/' + RouterUrls.BrowseProjects;
    window.open(url, '_blank');
  }

  redirectToProject(slug: string) {
    const url = this.languageService.getCurrentLangWithSlash() + '/' + RouterUrls.BrowseProjects + '/' + slug;
    window.open(url, '_blank');
  }

  onFocusLost(fieldName: string): void {
    // We force all fields validation, as validation is manual on submit or focus change
    this.form.updateValueAndValidity();
    // If its valid, we hide errors
    if (this.form.valid) {
      this.showErrors = false;
    }
    // If the field that lost focus has errors, we display them
    if (this.form.get(fieldName)?.invalid) {
      this.showErrors = true;
    }
    // We set the error message and update it if the error changed
    this.setErrorMessage();
  }

  /**
   * Updates the error message based on the current state of the form validation.
   * This method checks for specific errors in the form, applies custom errors
   * if necessary, and determines the most relevant error message to display.
   */
  setErrorMessage(): void {
    // We check error fieldsMismatch on the form as this detection is manual
    if (this.form.hasError('fieldsMismatch')) {
      this.form.get('confirmEmail')?.setErrors({ fieldsMismatch: true });
    }

    // Error codes and messages sorted by priority
    const errorChecks = [
      { control: this.form.get('email'),
        errors: { 'required': 'SIGN_UP_PAGE.email_required_signup', 'pattern': 'SIGN_UP_PAGE.email_structure_signup' }},
      { control: this.form.get('confirmEmail'),
        errors: { 'required': 'SIGN_UP_PAGE.email_repeat_signup', 'fieldsMismatch': 'SIGN_UP_PAGE.different_mail_signup' } },
      { control: this.form.get('termsChecked'), errors: { 'required': 'SIGN_UP_PAGE.terms_signup' } }
    ];

    // Iterate over each control to check for any active errors.
    for (const { control, errors } of errorChecks) {
      if (control?.touched && control.errors) {
        const firstErrorKey = Object.keys(control.errors).find(key => errors[key]);
        if (firstErrorKey) {
          this.errorMessage = errors[firstErrorKey];
          // Update lastError if a new error message is found
          // and disable error visualization as we don't want to stress the user
          if (this.lastError !== this.errorMessage) {
            this.lastError = this.errorMessage;
            this.showErrors = false;
          }
          break;
        }
      }
    }
  }

  touchedAndInvalid(fieldName: string) {
    return this.showErrors && this.form.get(fieldName).touched && this.form.get(fieldName).invalid
  }

  /**
   * Returns necessary styles to truncate text by number of lines with only CSS.
   * @param shortDescriptionText The short description text to be truncated.
   * @returns A style object for truncating long text.
   */
  getTruncatedStyle(shortDescriptionText: Language): any {

    return {
      'display': '-webkit-box',
      '-webkit-line-clamp': '5',
      '-webkit-box-orient': 'vertical',
      'overflow': 'hidden',
      'height': 'auto'
    };
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.brandsService.isBrandPage.next(false);
  }
}
